import {Component, inject, Input, OnDestroy} from '@angular/core';
import {MatCardModule} from "@angular/material/card";
import {AgGridAngular} from "ag-grid-angular";
import {ColDef, GridApi, GridReadyEvent, RowClickedEvent} from "ag-grid-community";
import {MatButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {IGamePlayerInputData} from "../../../interfaces/player/IGamePlayerInputData";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {IPlayedPlayerNumbersByGroupRow} from "../../../interfaces/player/IPlayedPlayerNumbersByGroupRow";
import {PlayersService} from "../../../services/players.service";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {PlayerInfoTableDefs} from "../../player-game-info/player-info-table-defs";
import {MatDialog} from "@angular/material/dialog";
import {concatMap, Subscription, tap} from "rxjs";
import {
  DeactivatePlayerNumbersComponent
} from "../../dialogs/deactivate-player-numbers/deactivate-player-numbers.component";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {SnackbarService} from "../../../services/snackbar.service";
import {IGoldRushTicketStripQueryResult} from "../../../interfaces/player/IGoldRushTicketStripQueryResult";
import {IGoldRushStrip} from "../../../interfaces/IGoldRushStrip";
import {IFailedAutoplayStripsRow} from "../../../interfaces/player/IFailedAutoplayStripsRow";
import {FailedAutoplayDetailsComponent} from "../../dialogs/failed-autoplay-details/failed-autoplay-details.component";

@Component({
  selector: 'app-manage-player-gold-rush-strips',
  standalone: true,
  imports: [
    MatCardModule,
    AgGridAngular,
    MatButton,
    MatIcon,
    MatProgressSpinner,
  ],
  templateUrl: './manage-player-gold-rush-strips.component.html',
  styleUrl: './manage-player-gold-rush-strips.component.css'
})
export class ManagePlayerGoldRushStripsComponent implements OnDestroy {

  private playerService: PlayersService = inject(PlayersService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private playerInfoTables: PlayerInfoTableDefs = inject(PlayerInfoTableDefs);
  private snackBarService: SnackbarService = inject(SnackbarService);
  private matDialog: MatDialog = inject(MatDialog);

  @Input()
  public set gamePlayerData(gamePlayerDataP: IGamePlayerInputData) {
    if (gamePlayerDataP.playerId && gamePlayerDataP.game.Id) {
      this.unsubscribeFromFetchingStrips();
      this.clearGameData();
      this.game = gamePlayerDataP.game;
      this.playerId = gamePlayerDataP.playerId;
      this.setupColumnDefs();
      this.setupFetchStrips();
    }
  }

  public uiState: UIStateEnum = UIStateEnum.ShowLoading;
  public uiStateForTemplate = UIStateEnum;

  public failedAutoplayStrips: IFailedAutoplayStripsRow[] = [];
  public failedAutoplayStripsRawData: IGoldRushTicketStripQueryResult['FailedAutoplayStrips'] = [];
  public allStrips: IGoldRushStrip[] = [];
  public deactivatedStrips: IGoldRushStrip[] = [];
  public playedStripRows: IPlayedPlayerNumbersByGroupRow[] = [];

  public game: IGameQueryResult | null = null;
  public playerId: string = '';

  public playedStripsGridApi!: GridApi<IPlayedPlayerNumbersByGroupRow>;
  public allStripsGridApi!: GridApi<IGoldRushStrip>;
  public failedAutoplayStripsGridApi!: GridApi<IFailedAutoplayStripsRow>;
  public deactivatedStripsGridApi!: GridApi<IGoldRushStrip>;

  public playedStripsColumnDefs: ColDef[] = [];
  public allStripsColumnDefs: ColDef[] = [];
  public failedAutoplayColumnDefs: ColDef[] = [];
  public deactivatedStripsColumnDefs: ColDef[] = [];

  private fetchStripsSubscription: Subscription | null = null;

  ngOnDestroy(): void {
    this.unsubscribeFromFetchingStrips();
  }

  private unsubscribeFromFetchingStrips() {
    if (this.fetchStripsSubscription) {
      this.fetchStripsSubscription.unsubscribe();
    }
  }

  private setupColumnDefs() {
    this.allStripsColumnDefs = this.playerInfoTables.getColumnDefsForGameType(this.game!);
    this.playedStripsColumnDefs = this.playerInfoTables.playedNumberGroupsColumnDefs;
    this.deactivatedStripsColumnDefs = this.playerInfoTables.deactivatedStripsColumnDefs;
    this.failedAutoplayColumnDefs = this.playerInfoTables.failedAutoplayStripsColumnDefs;
  }

  private setupFetchStrips() {
    this.fetchStripsSubscription = this.playerService.fetchUpdatedTicketNumbersSubject$
      .pipe(
        concatMap(() => {
          this.uiState = UIStateEnum.ShowLoading;

          return this.playerService.getGoldRushStripsByPlayerIdAndGameId(this.playerId, this.game?.Id!)
            .pipe(
              tap(data => {
                this.setGoldRushStrips(data);
              })
            );
        })
      ).subscribe({
        error: (err) => {
          this.uiState = UIStateEnum.ShowData;
          this.errorHandlingService.displayPageLevelErrorMessage(err, 'There was an issue retrieving Gold Rush strips. Please try reloading.');
        }
      });
  }

  private setGoldRushStrips(stripData: IGoldRushTicketStripQueryResult) {
    this.playedStripRows = this.formatPlayedGoldRushStrips(stripData.PlayedStrips);
    this.failedAutoplayStripsRawData = stripData.FailedAutoplayStrips;
    this.failedAutoplayStrips = this.formatFailedAutoplayGoldRushStrips(stripData.FailedAutoplayStrips);
    this.allStrips = stripData.PlayedStrips.concat(stripData.NotPlayedStrips, stripData.FailedAutoplayStrips, stripData.PaymentProcessing);
    this.deactivatedStrips = stripData.DeactivatedStrips;
    this.uiState = UIStateEnum.ShowData;
  }

  private formatPlayedGoldRushStrips(strips: IGoldRushStrip[]): IPlayedPlayerNumbersByGroupRow[] {
    let rows: IPlayedPlayerNumbersByGroupRow[] = [];

    if (strips && strips.length > 0) {
      strips.forEach(strip => {
        rows.push({
          TicketNumbers: strip.TicketNumbers.join(", "),
          Played: true,
          Autoplay: strip.Autoplay,
          PurchasedViaAutoplay: strip.PurchasedViaAutoplay,
          Id: strip.Id
        });
      });
    }

    return rows;
  }

  private formatFailedAutoplayGoldRushStrips(strips: IGoldRushStrip[]): IFailedAutoplayStripsRow[] {
    let rows: IFailedAutoplayStripsRow[] = [];

    if (strips && strips.length > 0) {
      strips.forEach(strip => {
        rows.push({
          Id: strip.Id,
          TicketNumbers: strip.TicketNumbers.join(", "),
          PaymentTierString: strip.PaymentTierId
        });
      });
    }

    return rows;
  }

  public onPlayedStripsGridReady(params: GridReadyEvent<IPlayedPlayerNumbersByGroupRow>) {
    this.playedStripsGridApi = params.api;
    this.playedStripsGridApi.sizeColumnsToFit({
      defaultMinWidth: 100,
    });
  }

  public onAllStripsGridReady(params: GridReadyEvent<IGoldRushStrip>) {
    this.allStripsGridApi = params.api;
    this.allStripsGridApi.sizeColumnsToFit({
      defaultMinWidth: 100,
    });
  }

  public onDeactivatedStripsGridReady(params: GridReadyEvent<IGoldRushStrip>) {
    this.deactivatedStripsGridApi = params.api;
    this.deactivatedStripsGridApi.sizeColumnsToFit({
      defaultMinWidth: 100,
    });
  }

  public onFailedAutoplayStripsGridReady(params: GridReadyEvent<IFailedAutoplayStripsRow>) {
    this.failedAutoplayStripsGridApi = params.api;
    this.failedAutoplayStripsGridApi.sizeColumnsToFit({
      defaultMinWidth: 100,
    });
  }

  public onFailedAutoplayStripRowClick(event: RowClickedEvent<IFailedAutoplayStripsRow>) {
    const clickedStrip = event.data;
    // Find the corresponding raw data that has the AutoplayFailureDetails
    const rawStrip = this.failedAutoplayStripsRawData.find(strip => strip.Id === clickedStrip?.Id);

    if (rawStrip && clickedStrip) {
      this.matDialog.open(FailedAutoplayDetailsComponent, {
        width: '650px',
        data: {
          strip: clickedStrip,
          autoplayFailureDetails: rawStrip.AutoplayFailureDetails
        }
      });
    }
  }

  public onDeactivateStripsClick() {
    const dialogRef = this.matDialog.open(DeactivatePlayerNumbersComponent, {
      width: '650px',
      data: {
        PlayerId: this.playerId,
        Game: this.game,
        AllPlayerTickets: this.allStrips
      }
    });

    dialogRef.afterClosed().subscribe({
      next: (updateSuccessful: boolean) => {
        if (updateSuccessful) {
          this.snackBarService.openSuccessfulSnackBar('You have successfully deactivated the player\'s strips');
          this.playerService.fetchUpdatedTicketNumbersSubject.next(true);
        }
      }
    });
  }

  private clearGameData() {
    this.allStrips = [];
    this.playedStripRows = [];
    this.failedAutoplayStrips = [];
    this.failedAutoplayStripsRawData = [];
    this.uiState = UIStateEnum.ShowLoading;
  }
}
